import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useToast } from "@/components/feature/ToastProvider";
import type { VoteBrief, VoteDetail } from "@/lib/types";
import { submitVote } from "../api";
import { formatBannerCountdown, typeLabel } from "../utils";

interface Props {
  brief: VoteBrief;
}

// 메인 «진행중인 투표» 카드.
// 질문이 1개(poll, single/multi)면 카드 안에서 바로 투표할 수 있고,
// 설문이거나 주관식/척도면 «참여하러 가기» 로 상세로 보낸다.
export default function VoteMainCard({ brief }: Props) {
  const { toast } = useToast();
  const now = useNow();
  const { data, serverTs, loading, reload } = useApi<VoteDetail>(
    `votes.php?no=${brief.no}`
  );
  const [picked, setPicked] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const remaining = Math.max(0, brief.end_in - elapsed);
  const cd = formatBannerCountdown(remaining);

  const q = data?.questions?.[0];
  const qCnt = data?.q_cnt ?? brief.q_cnt;
  const isInline =
    qCnt === 1 && !!q && (q.qtype === "single" || q.qtype === "multi");
  const multi = q?.qtype === "multi";
  const joined = brief.is_joined === 1 || data?.is_joined === 1;
  const canResult = (data?.can_result ?? brief.can_result) === 1;
  const showResult = joined && canResult;

  const toggle = (optNo: number) => {
    if (!q) return;
    if (q.qtype === "single") {
      setPicked([optNo]);
      return;
    }
    if (picked.includes(optNo)) {
      setPicked(picked.filter((o) => o !== optNo));
      return;
    }
    if ((q.max_pick ?? 0) > 0 && picked.length >= (q.max_pick ?? 0)) return;
    setPicked([...picked, optNo]);
  };

  const handleVote = async () => {
    if (!q || picked.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const fields: [string, string][] = picked.map((o) => [
        `ans[${q.no}]${multi ? "[]" : ""}`,
        String(o),
      ]);
      const res = await submitVote(brief.no, fields);
      toast(res.msg || "참여해 주셔서 감사합니다.", "success");
      setPicked([]);
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "참여에 실패했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="flex flex-col rounded-xl border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
          {typeLabel(brief.vtype, brief.type_txt)}
        </span>
        {brief.is_anon === 1 && (
          <span className="inline-block rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
            익명
          </span>
        )}
        {joined && (
          <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
            참여완료
          </span>
        )}
      </div>

      <Link
        to={`/vote/${brief.no}`}
        className="mt-3 font-heading text-base font-bold text-foreground-950 hover:text-primary-700"
      >
        {brief.subject || brief.title || "투표"}
      </Link>

      {/* 본문: 인라인 투표 / 결과 / 안내 */}
      <div className="mt-4 flex-1">
        {loading ? (
          <div className="space-y-2.5">
            <div className="h-9 w-full animate-pulse rounded-lg bg-background-200"></div>
            <div className="h-9 w-3/4 animate-pulse rounded-lg bg-background-200"></div>
          </div>
        ) : showResult && isInline ? (
          <div className="space-y-2.5">
            {(q?.options ?? []).map((opt) => {
              const mine = (q?.my?.opts ?? []).includes(opt.no);
              const pct = Math.max(0, Math.min(100, opt.pct ?? 0));
              return (
                <div key={opt.no}>
                  <div className="flex items-center justify-between text-xs">
                    <span
                      className={
                        mine
                          ? "font-semibold text-foreground-900"
                          : "text-foreground-600"
                      }
                    >
                      {opt.text}
                    </span>
                    <span className="tabular-nums text-foreground-400">
                      {opt.cnt}명 · {opt.pct}%
                    </span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-background-100">
                    <div
                      className={`h-full rounded-full ${
                        mine ? "bg-primary-500" : "bg-secondary-400"
                      }`}
                      style={{ width: `${pct}%` }}
                      aria-hidden="true"
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : isInline ? (
          <>
            <div className="space-y-2">
              {(q?.options ?? []).map((opt) => {
                const selected = picked.includes(opt.no);
                return (
                  <button
                    key={opt.no}
                    type="button"
                    onClick={() => toggle(opt.no)}
                    aria-pressed={selected}
                    className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left text-sm transition-colors ${
                      selected
                        ? "border-primary-400 bg-primary-50 font-semibold text-foreground-950"
                        : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-200 hover:bg-background-100"
                    }`}
                  >
                    <i
                      className={`${
                        selected
                          ? multi
                            ? "ri-checkbox-fill"
                            : "ri-checkbox-blank-circle-fill"
                          : multi
                            ? "ri-checkbox-blank-line"
                            : "ri-checkbox-blank-circle-line"
                      } ${selected ? "text-primary-600" : "text-foreground-300"}`}
                      aria-hidden="true"
                    ></i>
                    {opt.text}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={handleVote}
              disabled={picked.length === 0 || submitting}
              className="mt-3 w-full whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50"
            >
              {submitting ? "투표 중…" : "투표하기"}
            </button>
          </>
        ) : (
          <p className="rounded-lg bg-background-100 px-4 py-3 text-sm text-foreground-600">
            총 {qCnt}개 문항의 {typeLabel(brief.vtype, brief.type_txt)}입니다.
          </p>
        )}
      </div>

      {/* 하단: 참여수 + 카운트다운 / 참여하러 가기 */}
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3 text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <i className="ri-group-line" aria-hidden="true"></i>
          {brief.join_cnt}명 참여
        </span>
        {!isInline && (
          <Link
            to={`/vote/${brief.no}`}
            className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-primary-700 hover:text-primary-800"
          >
            참여하러 가기
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        )}
        {isInline && (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-time-line" aria-hidden="true"></i>
            종료까지{" "}
            <strong
              className={`tabular-nums ${
                cd.urgent ? "text-accent-700" : "text-foreground-700"
              }`}
            >
              {cd.text}
            </strong>
          </span>
        )}
      </div>
    </article>
  );
}